import { useCallback, useEffect, useState } from 'react';
import nlp from 'compromise';
import { stemmer } from 'stemmer';
import { throttle } from 'lodash';

// lowercase and remove puntuations.
const preprocessText = (text: string) => text.toLowerCase().replace(/[^\w\s]/g, '');

function findMatchIndexes(stemmedWords: string[], blackListedStemmedWords: string[]) {
  const blackListedStemmedWordSet = new Set(blackListedStemmedWords);

  // Find indexes of matching elements in array1
  const indexes = stemmedWords
    .map((item, index) => (blackListedStemmedWordSet.has(item) ? index : -1)) // Map to index or -1
    .filter((index) => index !== -1); // Filter out unmatched indexes

  return indexes;
}

const processText = (text: string, blacklist: string[]) => {
  const doc = nlp(preprocessText(text));
  const tokens = doc.terms().out('array');
  const stems = tokens.map((token: string) => stemmer(token));

  const blacklistWords: string[] = [];
  const blackListedPhrases: string[] = [];

  blacklist.forEach((badString) => {
    if (badString.includes(' ')) {
      blackListedPhrases.push(badString);
    } else {
      blacklistWords.push(badString);

      // Handle past
      const docPast = nlp(badString);
      docPast.verbs().toPastTense();
      const past = docPast.text();
      blacklistWords.push(past);
      const nounPast = nlp(past).nouns();
      nounPast.toSingular();
      blacklistWords.push(nounPast.all().out('text'));
      nounPast.toPlural();
      blacklistWords.push(nounPast.all().out('text'));

      // Handle future tense
      const docFuture = nlp(badString);
      docFuture.verbs().toFutureTense();
      const future = docFuture.text();
      blacklistWords.push(future);
      const nounFuture = nlp(future).nouns();
      nounFuture.toSingular();
      blacklistWords.push(nounFuture.all().out('text'));
      nounFuture.toPlural();
      blacklistWords.push(nounFuture.all().out('text'));

      // Handle present tense
      const docPresent = nlp(badString);
      docPresent.verbs().toPresentTense();
      const persent = docPresent.text();
      blacklistWords.push(persent);
      const nounPresent = nlp(persent).nouns();
      nounPresent.toSingular();
      blacklistWords.push(nounPresent.all().out('text'));
      nounPresent.toPlural();
      blacklistWords.push(nounPresent.all().out('text'));
    }
  });

  const blacklistedWordStems = blacklistWords.map((token: string) => stemmer(token.toLowerCase()));
  const uniqueBlackListedWordStems = Array.from(new Set(blacklistedWordStems));

  const blackListedPhrasesWithVariations: string[] = [];

  blackListedPhrases.forEach((pharse: string) => {
    blackListedPhrasesWithVariations.push(pharse);

    const docPast = nlp(pharse);

    // Handle past tense
    docPast.verbs().toPastTense();
    const past = docPast.text();
    blackListedPhrasesWithVariations.push(past);
    const nounPast = nlp(past).nouns();
    nounPast.toSingular();
    blackListedPhrasesWithVariations.push(nounPast.all().out('text'));
    nounPast.toPlural();
    blackListedPhrasesWithVariations.push(nounPast.all().out('text'));

    // Handle past tense
    const docFuture = nlp(pharse);
    docFuture.verbs().toFutureTense();
    const future = docFuture.text();
    blackListedPhrasesWithVariations.push(future);
    const nounFuture = nlp(future).nouns();
    nounFuture.toSingular();
    blackListedPhrasesWithVariations.push(nounFuture.all().out('text'));
    nounFuture.toPlural();
    blackListedPhrasesWithVariations.push(nounFuture.all().out('text'));

    // Handle past tense
    const docPresent = nlp(pharse);
    docPresent.verbs().toPresentTense();
    const persent = docPresent.text();
    blackListedPhrasesWithVariations.push(persent);
    const nounPresent = nlp(persent).nouns();
    nounPresent.toSingular();
    blackListedPhrasesWithVariations.push(nounPresent.all().out('text'));
    nounPresent.toPlural();
    blackListedPhrasesWithVariations.push(nounPresent.all().out('text'));

    docPresent.verbs().toInfinitive();
    blackListedPhrasesWithVariations.push(docPresent.text());
  });

  const matchIndexes = findMatchIndexes(stems, uniqueBlackListedWordStems);
  const identified: string[] = [];
  matchIndexes.forEach((index) => {
    identified.push(tokens[index]);
  });
  blackListedPhrasesWithVariations.forEach((phrase) => {
    if (text.includes(phrase)) {
      identified.push(phrase);
    }
  });
  return Array.from(new Set(identified)); // Remove duplicates
};

function useIdentifyBlackListedWords(value: string, blacklist: string[], debounceDuration = 1000) {
  const [identifiedWords, setIdentifiedWords] = useState<string[]>([]);

  const debouncedProcessText = useCallback(throttle(processText, debounceDuration), [processText]);

  useEffect(() => {
    const identified = debouncedProcessText(value, blacklist);
    // Avoid rerender for every char change
    // Only update state when indentified words array changes.
    if (identified && identifiedWords.join('') !== identified.join('')) {
      setIdentifiedWords(identified);
    }
  }, [value, blacklist]);

  return identifiedWords;
}

export default useIdentifyBlackListedWords;
